import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UploadSimple, FileXls } from "@phosphor-icons/react";
import { adminApi } from "@/lib/api";
import { Input } from "@/components/ui/input";

const money = (n) => (n == null ? "--" : `$${Number(n).toFixed(2)}`);

/** Spreadsheet → priced, auto-accepted invoice (same engine as a customer submission). */
export const UploadInvoicePanel = ({ onUploaded }) => {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", payment_method: "", notes: "", send_email: false });
  const fileRef = useRef(null);
  const [drag, setDrag] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("name", form.name.trim() || file.name.replace(/\.(csv|xlsx|xlsm)$/i, ""));
    fd.append("email", form.email.trim()); fd.append("payment_method", form.payment_method); fd.append("notes", form.notes); fd.append("send_email", form.send_email ? "true" : "false");
    try {
      const r = await adminApi.uploadDeal(fd);
      toast.success(`${r.order_no}: ${r.priced} sets priced at ${money(r.total)}${r.review ? ` · ${r.review} need a price` : ""}${r.skipped?.length ? ` · ${r.skipped.length} rows skipped` : ""}`);
      onUploaded?.(r);
      nav(`/invoices/${r.request_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="grid md:grid-cols-[1fr_320px] gap-4" data-testid="upload-invoice-panel">
      <label onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files?.[0]); }}
        className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-sm p-8 cursor-pointer transition-colors ${drag ? "border-[#E3000B] bg-[#E3000B]/5" : "border-[#E4E4E0] hover:border-black bg-white"}`} data-testid="deals-dropzone">
        <input ref={fileRef} type="file" accept=".csv,.xlsx,.xlsm,text/csv" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="deals-file-input" disabled={busy} />
        {busy ? <div className="font-mono text-sm animate-pulse" data-testid="deals-upload-busy">Pricing every set — PriceCharting + Amazon…</div> : (
          <>
            <UploadSimple size={28} />
            <div className="font-head font-black text-lg">Drop a CSV or XLSX to create an invoice</div>
            <div className="font-mono text-[11px] text-gray-500 text-center">Columns we read: <b>set</b> (or a title containing the set number), <b>qty</b>, optional <b>ask / cost</b>. Every row is priced like a customer submission and auto-accepted at the Smart Offer.</div>
            <span className="mt-2 h-9 px-4 inline-flex items-center gap-2 rounded-sm bg-black text-white font-mono text-xs"><FileXls size={14} /> Choose file</span>
          </>
        )}
      </label>
      <div className="border border-[#E4E4E0] rounded-sm p-4 space-y-2 bg-white">
        <div className="text-[10px] font-black tracking-widest text-gray-500">SELLER DETAILS (OPTIONAL · EDITABLE LATER)</div>
        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Seller / deal name (defaults to file name)" className="h-9 bg-[#F6F6F3] border-[#E4E4E0] font-mono text-xs" data-testid="deals-name" />
        <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Seller email" className="h-9 bg-[#F6F6F3] border-[#E4E4E0] font-mono text-xs" data-testid="deals-email" />
        <select value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} className="h-9 w-full rounded-sm border border-[#E4E4E0] bg-[#F6F6F3] px-2 font-mono text-xs" data-testid="deals-paymethod">
          <option value="">Payout method…</option><option value="venmo">Venmo</option><option value="cashapp">Cash App</option><option value="paypal">PayPal</option>
        </select>
        <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes (where the lot came from, pickup…)" className="h-9 bg-[#F6F6F3] border-[#E4E4E0] font-mono text-xs" data-testid="deals-notes" />
        <label className="flex items-center gap-2 font-mono text-[11px] text-gray-700 pt-1">
          <input type="checkbox" checked={form.send_email} onChange={(e) => setForm({ ...form, send_email: e.target.checked })} data-testid="deals-send-email" /> Email the seller their invoice link
        </label>
      </div>
    </div>
  );
};
